import React, { useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { motion } from 'framer-motion';
import LogoHeader from '@/components/invoice/LogoHeader';
import PaymentMethods from '@/components/invoice/PaymentMethods';
import Footer from '../components/landing/Footer';

export default function InvoicePayment() {
  const isMobile = useIsMobile();
  const [amount, setAmount] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [paymentType, setPaymentType] = useState('one-time');
  const [interval, setInterval] = useState('month');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const isSuccess = urlParams.get('success');
  const isCanceled = urlParams.get('canceled');

  const handlePayment = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!amount || parseFloat(amount) <= 0) {
        setError('Please enter a valid amount');
        setLoading(false);
        return;
      }

      if (!email) {
        setError('Please enter a valid email');
        setLoading(false);
        return;
      }

      const response = await base44.functions.invoke('createCheckoutSession', {
        amount: parseFloat(amount),
        paymentType,
        interval,
        email,
        description,
      });

      if (response.data.sessionUrl) {
        window.location.href = response.data.sessionUrl;
      } else {
        setError('Failed to create payment session');
      }
    } catch (err) {
      const reason = err.response?.data?.error || err.data?.error || err.message;
      setError(`Payment could not be started${reason ? `: ${reason}` : '.'} Please try again or contact us.`);
      base44.functions.invoke('reportPaymentError', {
        error: `${reason || 'Unknown error'}${err.response?.status ? ` (status ${err.response.status})` : ''}`,
        email, amount, description, page: window.location.href, userAgent: navigator.userAgent,
      }).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <LogoHeader />
      <div className={`flex items-center justify-center p-4 ${isMobile ? "pt-20" : "pt-32"}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {isSuccess && (
          <Card className="mb-6 border-accent/30 bg-accent/5">
            <CardContent className="pt-6 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-foreground">Payment started!</p>
                <p className="text-sm text-muted-foreground mt-1">Thanks for starting the payment on your invoice. You will receive a follow up email after you complete the 2nd step.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {isCanceled && (
          <Card className="mb-6 border-destructive/30 bg-destructive/5">
            <CardContent className="pt-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-foreground">Payment canceled</p>
                <p className="text-sm text-muted-foreground mt-1">Your payment was not completed.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Invoice Payment</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-6 text-sm text-muted-foreground leading-relaxed">
              Start your invoice payment below. In the next step you will enter your details and ACH, credit or debit card details with our secure 3rd party payment processor Stripe.<br/><br/>Thank you.
            </p>
            <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-sm text-blue-900">
                If you need to update your credit card details with the same subscription visit our{' '}
                <a
                  href="https://billing.stripe.com/p/login/dR6bLZg4obO6eDm144"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:opacity-80"
                >
                  Stripe Customer Portal
                </a>
                .
              </p>
            </div>
            <form onSubmit={handlePayment} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base font-semibold">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-base font-semibold">
                  Description or Invoice #
                </Label>
                <Input
                  id="description"
                  type="text"
                  placeholder="e.g., Invoice #12345 or Monthly hosting"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Payment Type Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Payment Type</Label>
                <RadioGroup value={paymentType} onValueChange={setPaymentType}>
                  <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
                    <RadioGroupItem value="one-time" id="one-time" />
                    <Label htmlFor="one-time" className="cursor-pointer flex-1 font-medium m-0">
                      One-Time Payment
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
                    <RadioGroupItem value="subscription" id="subscription" />
                    <Label htmlFor="subscription" className="cursor-pointer flex-1 font-medium m-0">
                      Subscription
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Subscription Interval */}
              {paymentType === 'subscription' && (
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Billing Interval</Label>
                  <RadioGroup value={interval} onValueChange={setInterval}>
                    <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
                      <RadioGroupItem value="month" id="month" />
                      <Label htmlFor="month" className="cursor-pointer flex-1 font-medium m-0">
                        Monthly
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors">
                      <RadioGroupItem value="year" id="year" />
                      <Label htmlFor="year" className="cursor-pointer flex-1 font-medium m-0">
                        Annual
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Amount Input */}
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-base font-semibold">
                  Amount (USD)
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">$</span>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    step="0.01"
                    min="0"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/30 flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <PaymentMethods />

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !amount || !email}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay $${amount || '0.00'}`
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
      </div>
      <Footer />
    </div>
  );
}